// components/AdvancedShareModal.jsx
// 🚀 ULTIMATE SHARING EXPERIENCE
// One-tap sharing to all major platforms with smart optimizations

import React, { useState, useEffect } from 'react';
import { X, Download, Copy, Share2, Instagram, Twitter } from 'lucide-react';
import {
  generateShareLinks,
  copyImageToClipboard,
  copyTextToClipboard,
  detectPlatform,
  smartShare,
  getPrefillTemplates
} from '../utils/ShareOptimizer';

export default function AdvancedShareModal({ imageBlob, trial, verdict, onClose }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [shareLinks, setShareLinks] = useState(null);
  const [prefillTexts, setPrefillTexts] = useState(null);
  
  useEffect(() => {
    const detectedPlatform = detectPlatform();
    setPlatform(detectedPlatform);
    
    const imageUrl = URL.createObjectURL(imageBlob);
    setShareLinks(generateShareLinks(trial, verdict, imageUrl));
    setPrefillTexts(getPrefillTemplates(trial, verdict));
  }, [imageBlob, trial, verdict]);

  const handleQuickShare = async (method) => {
    setSharing(true);
    
    try {
      const result = await smartShare(trial, verdict, imageBlob, method);
      
      if (result.success) {
        // Show success message
        setTimeout(() => onClose(), 1500);
      }
    } catch (error) {
      console.error('Share failed:', error);
      alert('Share failed. Try downloading the image instead!');
    } finally {
      setSharing(false);
    }
  };

  const handleCopyImage = async () => {
    const success = await copyImageToClipboard(imageBlob);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      alert('Clipboard not supported. Download the image instead!');
    }
  };

  const handleCopyCaption = async (text) => {
    const success = await copyTextToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const url = URL.createObjectURL(imageBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gweffit_verdict_${trial.id}_${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!shareLinks || !prefillTexts) return null;

  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const winnerPct = Math.round(verdict.winnerPct || 50);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-slate-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-purple-500">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-8 py-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-3xl font-black text-white mb-1">Share Your Verdict</h2>
            <p className="text-slate-400 text-sm">One tap to all platforms</p>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-slate-700 rounded-xl transition-colors"
          >
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-6 border-b border-slate-700">
          <div className="aspect-square bg-slate-900 rounded-2xl overflow-hidden mb-4">
            <img 
              src={URL.createObjectURL(imageBlob)} 
              alt="Verdict meme"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-white mb-1">
              {winner} wins {winnerPct}%!
            </div>
            <div className="text-slate-400 text-sm">
              {trial.totalVotes || 0} jurors voted
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-6 border-b border-slate-700">
          <div className="text-sm font-bold text-slate-400 mb-3 uppercase tracking-wider">
            Quick Actions
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Smart Share Button */}
            {(platform?.mobile && navigator.share) && (
              <button
                onClick={() => handleQuickShare('auto')}
                disabled={sharing}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Share2 className="w-8 h-8" />
                <span className="font-bold text-sm">Share</span>
              </button>
            )}
            
            {/* Copy to Clipboard */}
            <button
              onClick={handleCopyImage}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-all transform hover:scale-105"
            >
              <Copy className="w-8 h-8" />
              <span className="font-bold text-sm">
                {copied ? '✓ Copied!' : 'Copy Image'}
              </span>
            </button>
            
            {/* Download */}
            <button
              onClick={handleDownload}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-all transform hover:scale-105"
            >
              <Download className="w-8 h-8" />
              <span className="font-bold text-sm">Download</span>
            </button>

            {/* Instagram Direct */}
            {(platform?.instagram || platform?.mobile) && (
              <button
                onClick={async () => {
                  await handleCopyImage();
                  setTimeout(() => {
                    window.open('instagram://story-camera', '_blank');
                  }, 500);
                }}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white transition-all transform hover:scale-105"
              >
                <Instagram className="w-8 h-8" />
                <span className="font-bold text-sm">Instagram</span>
              </button>
            )}
          </div>
        </div>

        {/* Platform-Specific Sharing */}
        <div className="p-6 border-b border-slate-700">
          <div className="text-sm font-bold text-slate-400 mb-3 uppercase tracking-wider">
            Share To Platform
          </div>
          <div className="space-y-2">
            {/* Twitter */}
            <a
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 rounded-xl bg-slate-700 hover:bg-slate-600 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Twitter className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-white">Twitter / X</div>
                  <div className="text-sm text-slate-400">Post with caption</div>
                </div>
              </div>
              <div className="text-slate-400 group-hover:text-white transition-colors">
                →
              </div>
            </a>

            {/* Facebook */}
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 rounded-xl bg-slate-700 hover:bg-slate-600 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                  f
                </div>
                <div>
                  <div className="font-bold text-white">Facebook</div>
                  <div className="text-sm text-slate-400">Share on timeline</div>
                </div>
              </div>
              <div className="text-slate-400 group-hover:text-white transition-colors">
                →
              </div>
            </a>

            {/* Reddit */}
            <a
              href={shareLinks.reddit}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 rounded-xl bg-slate-700 hover:bg-slate-600 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center text-white font-bold">
                  r/
                </div>
                <div>
                  <div className="font-bold text-white">Reddit</div>
                  <div className="text-sm text-slate-400">Post to subreddit</div>
                </div>
              </div>
              <div className="text-slate-400 group-hover:text-white transition-colors">
                →
              </div>
            </a>

            {/* WhatsApp */}
            <a
              href={shareLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 rounded-xl bg-slate-700 hover:bg-slate-600 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                  W
                </div>
                <div>
                  <div className="font-bold text-white">WhatsApp</div>
                  <div className="text-sm text-slate-400">Send to friends</div>
                </div>
              </div>
              <div className="text-slate-400 group-hover:text-white transition-colors">
                →
              </div>
            </a>
          </div>
        </div>

        {/* Pre-written Captions */}
        <div className="p-6">
          <div className="text-sm font-bold text-slate-400 mb-3 uppercase tracking-wider">
            Pre-Written Captions
          </div>
          <div className="space-y-3">
            {/* Twitter Casual */}
            <div className="p-4 rounded-xl bg-slate-700">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-xs font-bold text-purple-400">TWITTER - CASUAL</div>
                <button
                  onClick={() => handleCopyCaption(prefillTexts.twitter.casual)}
                  className="text-xs font-bold text-slate-400 hover:text-white transition-colors"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-white whitespace-pre-wrap">
                {prefillTexts.twitter.casual}
              </div>
            </div>

            {/* Twitter Hype */}
            <div className="p-4 rounded-xl bg-slate-700">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-xs font-bold text-purple-400">TWITTER - HYPE</div>
                <button
                  onClick={() => handleCopyCaption(prefillTexts.twitter.hype)}
                  className="text-xs font-bold text-slate-400 hover:text-white transition-colors"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-white whitespace-pre-wrap">
                {prefillTexts.twitter.hype}
              </div>
            </div>

            {/* Instagram Caption */}
            <div className="p-4 rounded-xl bg-slate-700">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-xs font-bold text-purple-400">INSTAGRAM</div>
                <button
                  onClick={() => handleCopyCaption(prefillTexts.instagram.caption)}
                  className="text-xs font-bold text-slate-400 hover:text-white transition-colors"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-white whitespace-pre-wrap">
                {prefillTexts.instagram.caption}
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        {platform?.mobile && (
          <div className="px-6 pb-6">
            <div className="p-4 rounded-xl bg-purple-900/30 border border-purple-500/30">
              <div className="text-sm font-bold text-purple-400 mb-2">
                💡 Pro Tip:
              </div>
              <div className="text-sm text-slate-300">
                {platform.instagram 
                  ? "Tap 'Copy Image' then open Instagram. The image will be ready to paste into your story!"
                  : "Tap 'Share' for the easiest way to send to all your apps at once!"
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
